import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Nfc, Palette, Link2, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { LogoMark } from "@/components/Logo";
import { api } from "../lib/api";
import { useLanguage } from "../i18n";

const STEPS = [
  { num: "01", title: "Avvicina", desc: "Il cliente avvicina lo smartphone alla carta o all'adesivo NFC." },
  { num: "02", title: "Si apre la pagina", desc: "Il link si apre automaticamente sulla tua pagina delle recensioni Google." },
  { num: "03", title: "Recensione", desc: "Il cliente lascia la sua opinione in pochi secondi, senza installare alcuna app." },
];

const BENEFITS = [
  { icon: Palette, title: "Design personalizzato", desc: "Carte e adesivi con il nome e i colori della tua attività." },
  { icon: Link2, title: "Configurazione inclusa", desc: "Colleghiamo ogni supporto al tuo link recensioni di Google." },
  { icon: Zap, title: "Pronti all'uso", desc: "Nessuna app e nessuna configurazione per i tuoi clienti." },
];

const EMPTY = { name: "", email: "", business: "", google_link: "", notes: "", privacy: false, website: "" };

const NfcCard = () => {
  const { t } = useLanguage();
  const [hasPhoto, setHasPhoto] = useState(false);
  return (
    <div className="relative mx-auto w-full max-w-sm" data-testid="nfc-card-mockup">
      <img
        src="/assets/nfc-carte.jpg"
        alt={t("Le carte NFC di Alsera Studio")}
        onLoad={() => setHasPhoto(true)}
        onError={() => setHasPhoto(false)}
        className={`relative aspect-square w-full rounded-3xl object-cover shadow-2xl shadow-petrolio/30 ${hasPhoto ? "" : "hidden"}`}
        data-testid="nfc-card-photo"
      />
      {!hasPhoto && (
        <>
      <div className="absolute inset-0 -rotate-6 rounded-3xl bg-acqua" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 30, rotate: 3 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="relative flex aspect-[8/5] flex-col justify-between overflow-hidden rounded-3xl bg-petrolio p-6 text-avorio shadow-2xl shadow-petrolio/30"
      >
        <div className="flex items-start justify-between">
          <LogoMark className="h-10 w-10" />
          <Nfc className="h-7 w-7 text-elettrico" />
        </div>
        <div>
          <p className="font-display text-xl font-bold tracking-tight">{t("La tua attività")}</p>
          <p className="mt-1 text-sm text-avorio/60">{t("Tocca per recensire")}</p>
        </div>
      </motion.div>
        </>
      )}
    </div>
  );
};

export default function NfcRecensioni() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error(t("Devi accettare l'informativa privacy per inviare la richiesta."));
      return;
    }
    setSending(true);
    try {
      const details = [
        form.google_link.trim() && `Link scheda Google: ${form.google_link.trim()}`,
        form.notes.trim(),
      ].filter(Boolean).join("\n\n");
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        company: form.business,
        service_type: "Recensioni NFC",
        budget: "",
        message: details || "Richiesta di carte/adesivi NFC per la mia attività.",
        privacy: form.privacy,
        website: form.website,
      });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("Errore durante l'invio. Riprova."));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title={`${t("Recensioni NFC")} — Alsera Studio`}
        description={t("Carte e adesivi NFC personalizzati che portano i clienti direttamente alla tua pagina delle recensioni Google: basta avvicinare lo smartphone.")}
      />

      {/* HERO */}
      <section className="noise-overlay relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-24" data-testid="nfc-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-acqua/60 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">{t("Recensioni NFC")}</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-antracite sm:text-5xl" data-testid="nfc-title">
              {t("Le recensioni dei tuoi clienti, a un tocco di distanza.")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-antracite/75 sm:text-lg" data-testid="nfc-subtitle">
              {t("Carte e adesivi NFC personalizzati che portano i clienti direttamente alla tua pagina delle recensioni Google: basta avvicinare lo smartphone.")}
            </p>
            <div className="mt-9">
              <a href="#ordina" className="btn-primary" data-testid="nfc-cta-order">
                {t("Richiedi le tue carte NFC")}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <NfcCard />
          </Reveal>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="nfc-how">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Come funziona")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Semplice per te, immediato per i tuoi clienti.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid items-center gap-12 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <figure className="mx-auto max-w-sm" data-testid="nfc-tap-demo">
                <img
                  src="/assets/nfc-tap-demo.gif"
                  alt={t("Animazione: il telefono si avvicina alla carta NFC e si apre la pagina delle recensioni")}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full rounded-3xl border border-antracite/10 object-cover shadow-xl shadow-petrolio/15"
                  data-testid="nfc-tap-demo-gif"
                />
                <figcaption className="mt-3 text-center text-xs font-semibold uppercase tracking-[0.14em] text-antracite/50">
                  {t("Guarda quanto è semplice")}
                </figcaption>
              </figure>
            </Reveal>
            <div className="grid gap-10 sm:grid-cols-3 lg:col-span-3 lg:grid-cols-1 lg:gap-8">
              {STEPS.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.08}>
                  <div className="lg:flex lg:items-start lg:gap-6" data-testid={`nfc-step-${i}`}>
                    <span className="font-display text-4xl font-extrabold text-elettrico/25">{s.num}</span>
                    <div>
                      <h3 className="mt-3 text-lg font-bold tracking-tight text-antracite lg:mt-0">{t(s.title)}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-antracite/70 sm:text-base">{t(s.desc)}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COSA RICEVI */}
      <section className="py-24 sm:py-28" data-testid="nfc-benefits">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Cosa ricevi")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Tutto pronto, pensato per la tua attività.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="group card-soft h-full p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`nfc-benefit-${i}`}>
                  <div className="mb-5 inline-flex rounded-2xl bg-acqua p-3 text-petrolio transition-colors duration-500 group-hover:bg-petrolio group-hover:text-avorio">
                    <b.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight text-antracite">{t(b.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(b.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex items-start gap-4 rounded-3xl border border-elettrico/25 bg-acqua/40 p-6 sm:p-8" data-testid="nfc-note">
              <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-elettrico" />
              <div>
                <p className="font-bold text-antracite">{t("Trasparenza prima di tutto")}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-antracite/70 sm:text-base">
                  {t("Alsera Studio non è affiliata a Google. Le recensioni sono lasciate liberamente dai tuoi clienti: nessun incentivo, nessun filtro e nessuna garanzia sul numero di recensioni.")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ESEMPI REALI */}
      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="nfc-examples">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Esempi reali")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Le carte NFC, dal vivo.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {["/assets/nfc-carta-bianca.jpg", "/assets/nfc-carte.jpg", "/assets/nfc-carta-feedback.jpg"].map((src, i) => (
              <Reveal key={src} delay={i * 0.08}>
                <img
                  src={src}
                  alt={t("Le carte NFC di Alsera Studio")}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full rounded-3xl border border-antracite/10 object-cover shadow-sm transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10"
                  data-testid={`nfc-example-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODULO ORDINE */}
      <section id="ordina" className="scroll-mt-24 pb-24 sm:pb-28" data-testid="nfc-order">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Ordina")}</p>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Richiedi le tue carte NFC")}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-antracite/70">
              {t("Raccontaci la tua attività: ti ricontattiamo con una proposta su misura, senza impegno.")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-soft mt-10 p-8 sm:p-10">
              {sent ? (
                <div className="py-6 text-center" data-testid="nfc-success">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-acqua text-petrolio">
                    <Check className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 text-xl font-bold text-antracite">{t("Richiesta inviata.")}</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-antracite/70">
                    {t("Ti ricontatteremo al più presto con la tua proposta personalizzata.")}
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="nfc-form">
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
                    <label htmlFor="nfc-website">Sito web</label>
                    <input id="nfc-website" type="text" data-testid="nfc-website-honeypot" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="nfc-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Nome e cognome")} *</label>
                      <input id="nfc-name" data-testid="nfc-name" required minLength={2} maxLength={80} value={form.name} onChange={(e) => set("name", e.target.value)} className="input-field" />
                    </div>
                    <div>
                      <label htmlFor="nfc-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Email")} *</label>
                      <input id="nfc-email" type="email" data-testid="nfc-email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="input-field" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="nfc-business" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Nome dell'attività")} *</label>
                    <input id="nfc-business" data-testid="nfc-business" required minLength={2} maxLength={80} value={form.business} onChange={(e) => set("business", e.target.value)} className="input-field" />
                  </div>
                  <div>
                    <label htmlFor="nfc-google-link" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Link della tua scheda Google (facoltativo)")}</label>
                    <input id="nfc-google-link" type="url" data-testid="nfc-google-link" value={form.google_link} onChange={(e) => set("google_link", e.target.value)} className="input-field" placeholder="https://…" />
                  </div>
                  <div>
                    <label htmlFor="nfc-notes" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Note o richieste particolari (facoltativo)")}</label>
                    <textarea id="nfc-notes" data-testid="nfc-notes" rows={3} maxLength={600} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-field resize-none" />
                  </div>
                  <label className="flex items-start gap-3 text-sm text-antracite/70" data-testid="nfc-privacy-label">
                    <input type="checkbox" data-testid="nfc-privacy" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-1 h-4 w-4 rounded border-salvia/60 accent-petrolio" />
                    <span>{t("Ho letto l'informativa privacy e acconsento al trattamento dei miei dati per essere ricontattato. *")}</span>
                  </label>
                  <button type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60" data-testid="nfc-submit">
                    {sending ? t("Invio in corso…") : t("Invia la tua richiesta")}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
